import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion } from 'framer-motion'
import {
  Download,
  Upload,
  Archive,
  Trash2,
  RefreshCw,
  Clock,
  HardDrive,
  Users,
  AlertCircle,
  CheckCircle,
  Plus,
  Search,
  X
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import ConfirmationModal from '../common/ConfirmationModal'

const ClientBackupManager = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [selectedClient, setSelectedClient] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showBulkBackupConfirm, setShowBulkBackupConfirm] = useState(false)
  const [backupToDelete, setBackupToDelete] = useState(null)
  const [selectedBackup, setSelectedBackup] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [isCreatingBackup, setIsCreatingBackup] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(20)

  const { data: backupData = { backups: [], total: 0 }, isLoading, isFetching, error, refetch } = useQuery(
    ['backups', currentPage, pageSize, searchTerm, filterStatus],
    async () => {
      const response = await apiService.get('/backups', {
        params: {
          page: currentPage,
          limit: pageSize,
          search: searchTerm,
          status: filterStatus !== 'all' ? filterStatus : undefined
        }
      })
      return response.data
    },
    { refetchOnWindowFocus: false, staleTime: 30000, keepPreviousData: true }
  )

  const backups = backupData.backups || []
  const totalBackups = backupData.total || 0
  const totalPages = Math.ceil(totalBackups / pageSize)

  const { data: clientsData, isLoading: clientsLoading } = useQuery(
    ['clients'],
    () => apiService.get('/clients').then(res => res.data),
    { staleTime: 30000 }
  )
  const clients = clientsData?.clients || []

  const createBackupMutation = useMutation(
    async (clientId) => {
      const response = await apiService.post(`/backups/client/${clientId}`, { type: 'manual' })
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Backup created successfully')
        queryClient.invalidateQueries('backups')
        setShowCreateModal(false)
        setSelectedClient('')
        setIsCreatingBackup(false)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create backup')
        setIsCreatingBackup(false)
      }
    }
  )

  const deleteBackupMutation = useMutation(
    async (backupId) => { await apiService.delete(`/backups/${backupId}`) },
    {
      onSuccess: () => {
        toast.success('Backup deleted successfully')
        queryClient.invalidateQueries('backups')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete backup')
      }
    }
  )

  const bulkBackupMutation = useMutation(
    async (clientIds) => {
      const response = await apiService.post('/backups/bulk', { clientIds, type: 'manual' })
      return response.data
    },
    {
      onSuccess: (data) => {
        toast.success(`Bulk backup completed: ${data.summary.successful}/${data.summary.total}`)
        queryClient.invalidateQueries('backups')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create bulk backup')
      }
    }
  )

  const filteredBackups = backups

  const handleCreateBackup = async () => {
    if (!selectedClient) { toast.error('Please select a client'); return }
    setIsCreatingBackup(true)
    createBackupMutation.mutate(selectedClient)
  }

  const handleDeleteBackup = (backup) => { setBackupToDelete(backup); setShowDeleteConfirm(true) }
  const handleDeleteConfirm = () => {
    if (backupToDelete) {
      deleteBackupMutation.mutate(backupToDelete.id)
      setShowDeleteConfirm(false)
      setBackupToDelete(null)
    }
  }
  const handleDeleteCancel = () => { setShowDeleteConfirm(false); setBackupToDelete(null) }

  const handleDownloadBackup = async (backup) => {
    try {
      const response = await apiService.get(`/backups/${backup.id}/download`, {
        responseType: 'blob',
        headers: { 'Accept': 'application/zip' }
      })
      const blob = new Blob([response.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = backup.fileName.replace(/[^a-zA-Z0-9.-]/g, '_')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Backup downloaded successfully')
    } catch (error) {
      console.error('Download error:', error)
      toast.error(error.response?.data?.error || 'Failed to download backup')
    }
  }

  const handleRestoreBackup = (backup) => { setSelectedBackup(backup); setShowRestoreModal(true) }

  const confirmRestore = async () => {
    try {
      await apiService.post(`/backups/${selectedBackup.id}/restore`)
      toast.success('Backup restoration initiated')
      setShowRestoreModal(false)
      setSelectedBackup(null)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to restore backup')
    }
  }

  const handleBulkBackup = () => {
    if (!Array.isArray(clients) || clients.length === 0) { toast.error('No clients available for backup'); return }
    setShowBulkBackupConfirm(true)
  }

  const handleBulkBackupConfirm = () => {
    bulkBackupMutation.mutate(clients.map(c => c.id))
    setShowBulkBackupConfirm(false)
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle style={{ width: '1.25rem', height: '1.25rem', color: 'var(--success)' }} />
      case 'pending': return <Clock style={{ width: '1.25rem', height: '1.25rem', color: 'var(--warning)' }} />
      case 'failed': return <AlertCircle style={{ width: '1.25rem', height: '1.25rem', color: 'var(--danger)' }} />
      default: return <Clock style={{ width: '1.25rem', height: '1.25rem', color: 'var(--text-soft)' }} />
    }
  }

  if (isLoading) return <LoadingSpinner />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 className="font-bold" style={{ fontSize: '1.75rem' }}>Client Backup Manager</h1>
          <p className="text-muted">Create, manage, and restore client site backups</p>
        </div>
        <div className="flex items-center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary flex items-center gap-2"
            onClick={() => setShowCreateModal(true)}
            disabled={isCreatingBackup}
          >
            <Plus style={{ width: '1rem', height: '1rem' }} />
            Create Backup
          </button>
          <button
            className="btn btn-ghost flex items-center gap-2"
            onClick={handleBulkBackup}
            disabled={bulkBackupMutation.isLoading}
          >
            <Archive style={{ width: '1rem', height: '1rem' }} />
            Bulk Backup
          </button>
          <button
            className="btn btn-ghost btn-sm flex items-center gap-2"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw style={{ width: '1rem', height: '1rem' }} />
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
        {[
          { label: 'Total Backups', value: totalBackups, icon: Archive, color: 'var(--accent)' },
          {
            label: 'Storage Used',
            value: (() => {
              const storageBytes = backupData.totalStorageBytes
              const storageMB = backupData.totalStorageMB
              if (storageBytes && storageBytes > 0) return `${(storageBytes / (1024 * 1024)).toFixed(1)} MB`
              if (storageMB && storageMB > 0) return `${storageMB.toFixed(1)} MB`
              return '0.0 MB'
            })(),
            icon: HardDrive,
            color: 'var(--success)'
          },
          { label: 'Clients with Backups', value: backupData.uniqueClients || 0, icon: Users, color: 'var(--accent-3)' },
          { label: 'Successful', value: backupData.successfulBackups || 0, icon: CheckCircle, color: '#10b981' }
        ].map(({ label, value, icon: Icon, color }) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card card-pad"
          >
            <div className="flex items-center justify-between">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <p className="text-muted" style={{ fontSize: '0.875rem' }}>{label}</p>
                <p className="font-bold" style={{ fontSize: '1.5rem' }}>{value}</p>
              </div>
              <div style={{ padding: '0.75rem', borderRadius: 'var(--radius)', background: `${color}1a` }}>
                <Icon style={{ width: '1.5rem', height: '1.5rem', color }} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="card card-pad">
        <div className="flex items-center" style={{ gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, position: 'relative', minWidth: '200px' }}>
            <Search style={{
              position: 'absolute', left: '0.75rem',
              top: '50%', transform: 'translateY(-50%)',
              width: '1rem', height: '1rem',
              color: 'var(--text-soft)', pointerEvents: 'none'
            }} />
            <input
              type="text"
              placeholder="Search backups..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1) }}
              className="input w-full"
              style={{ paddingLeft: '2.25rem' }}
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1) }}
            className="input"
            style={{ width: '10rem' }}
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Backups List */}
      <div className="card overflow-hidden">
        {filteredBackups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
            <Archive style={{ width: '3rem', height: '3rem', color: 'var(--text-faint)', margin: '0 auto 0.5rem' }} />
            <h3 className="font-medium" style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>No backups found</h3>
            <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
              Get started by creating your first backup.
            </p>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {filteredBackups.map((backup) => (
              <li
                key={backup.id}
                style={{ borderBottom: '1px solid var(--border)' }}
              >
                <div style={{ padding: '1rem 1.5rem' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center" style={{ minWidth: 0 }}>
                      {getStatusIcon(backup.status)}
                      <div style={{ marginLeft: '1rem', minWidth: 0 }}>
                        <div className="flex items-center" style={{ gap: '0.5rem' }}>
                          <p className="font-medium truncate" style={{ fontSize: '0.875rem' }}>
                            {backup.fileName}
                          </p>
                          <span className="badge badge-success" style={{ flexShrink: 0 }}>
                            {backup.sitesCount} {backup.sitesCount === 1 ? 'site' : 'sites'}
                          </span>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.25rem', display: 'flex', gap: '0.5rem' }}>
                          <span>{backup.clientName}</span>
                          <span>·</span>
                          <span>{backup.fileSize}</span>
                          <span>·</span>
                          <span>{format(new Date(backup.createdAt), 'MMM dd, yyyy HH:mm')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center" style={{ gap: '0.25rem', flexShrink: 0, marginLeft: '1rem' }}>
                      <button
                        className="btn btn-ghost btn-icon"
                        onClick={() => handleDownloadBackup(backup)}
                        title="Download"
                        style={{ color: 'var(--accent)' }}
                      >
                        <Download style={{ width: '1rem', height: '1rem' }} />
                      </button>
                      <button
                        className="btn btn-ghost btn-icon"
                        onClick={() => handleRestoreBackup(backup)}
                        disabled={backup.status !== 'completed'}
                        title="Restore"
                        style={{ color: 'var(--success)' }}
                      >
                        <Upload style={{ width: '1rem', height: '1rem' }} />
                      </button>
                      <button
                        className="btn btn-ghost btn-icon"
                        onClick={() => handleDeleteBackup(backup)}
                        title="Delete"
                        style={{ color: 'var(--danger)' }}
                      >
                        <Trash2 style={{ width: '1rem', height: '1rem' }} />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="card card-pad">
          <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
            <div className="text-muted" style={{ fontSize: '0.875rem' }}>
              Showing {Math.min((currentPage - 1) * pageSize + 1, totalBackups)} to {Math.min(currentPage * pageSize, totalBackups)} of {totalBackups} backups
            </div>
            <div className="flex items-center" style={{ gap: '0.5rem' }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) pageNum = i + 1
                else if (currentPage <= 3) pageNum = i + 1
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i
                else pageNum = currentPage - 2 + i
                return (
                  <button
                    key={pageNum}
                    className={currentPage === pageNum ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Backup Modal */}
      {showCreateModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: '32rem' }}>
            <div className="flex items-center justify-between" style={{ padding: '1.5rem 1.5rem 1rem' }}>
              <h3 className="font-medium" style={{ fontSize: '1.125rem' }}>Create New Backup</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowCreateModal(false)}>
                <X style={{ width: '1.25rem', height: '1.25rem' }} />
              </button>
            </div>

            <div style={{ padding: '0 1.5rem 1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{
                  display: 'flex', gap: '0.75rem',
                  padding: '0.75rem',
                  background: 'var(--accent-soft)',
                  border: '1px solid var(--accent-3)',
                  borderRadius: 'var(--radius)'
                }}>
                  <Plus style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)', flexShrink: 0, marginTop: '0.125rem' }} />
                  <p style={{ fontSize: '0.875rem', color: 'var(--accent)' }}>
                    Create a new backup for a specific client's website and data.
                  </p>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                    Select Client
                  </label>
                  <select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    className="input w-full"
                    disabled={clientsLoading}
                  >
                    <option value="">{clientsLoading ? 'Loading clients...' : 'Choose a client...'}</option>
                    {Array.isArray(clients) && clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.company_name} ({client.email || 'No email'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end" style={{
              gap: '0.75rem',
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--border)',
              background: 'var(--surface-2)'
            }}>
              <button className="btn btn-ghost" onClick={() => setShowCreateModal(false)} disabled={isCreatingBackup}>
                Cancel
              </button>
              <button
                className="btn btn-primary flex items-center gap-2"
                onClick={handleCreateBackup}
                disabled={!selectedClient || isCreatingBackup}
              >
                {isCreatingBackup ? (
                  <><RefreshCw style={{ width: '1rem', height: '1rem' }} />Creating...</>
                ) : (
                  <><Plus style={{ width: '1rem', height: '1rem' }} />Create Backup</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Backup Modal */}
      {showRestoreModal && selectedBackup && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: '32rem' }}>
            <div className="flex items-center justify-between" style={{ padding: '1.5rem 1.5rem 1rem' }}>
              <h3 className="font-medium" style={{ fontSize: '1.125rem' }}>Restore Backup</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowRestoreModal(false)}>
                <X style={{ width: '1.25rem', height: '1.25rem' }} />
              </button>
            </div>

            <div style={{ padding: '0 1.5rem 1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{
                  display: 'flex', gap: '0.75rem',
                  padding: '0.75rem',
                  background: 'rgba(245,158,11,0.08)',
                  border: '1px solid rgba(245,158,11,0.3)',
                  borderRadius: 'var(--radius)'
                }}>
                  <AlertCircle style={{ width: '1.25rem', height: '1.25rem', color: 'var(--warning)', flexShrink: 0, marginTop: '0.125rem' }} />
                  <div>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--warning)', marginBottom: '0.25rem' }}>Warning</h4>
                    <p style={{ fontSize: '0.875rem', color: 'var(--warning)' }}>
                      Restoring this backup will overwrite existing site data. This action cannot be undone.
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium" style={{ marginBottom: '0.5rem' }}>Backup Details:</h4>
                  <div style={{
                    background: 'var(--surface-2)',
                    borderRadius: 'var(--radius)',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    display: 'flex', flexDirection: 'column', gap: '0.25rem'
                  }}>
                    <div><strong>File:</strong> {selectedBackup.fileName}</div>
                    <div><strong>Client:</strong> {selectedBackup.clientName}</div>
                    <div><strong>Sites:</strong> {selectedBackup.sitesCount}</div>
                    <div><strong>Size:</strong> {selectedBackup.fileSize}</div>
                    <div><strong>Created:</strong> {format(new Date(selectedBackup.createdAt), 'MMM dd, yyyy HH:mm')}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end" style={{
              gap: '0.75rem',
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--border)',
              background: 'var(--surface-2)'
            }}>
              <button className="btn btn-ghost" onClick={() => setShowRestoreModal(false)}>Cancel</button>
              <button className="btn btn-danger flex items-center gap-2" onClick={confirmRestore}>
                <AlertCircle style={{ width: '1rem', height: '1rem' }} />
                Restore Backup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteBackupMutation.isLoading}
        title="Delete Backup"
        message={
          <div>
            Are you sure you want to delete backup{' '}
            <span className="font-medium">{backupToDelete?.fileName}</span>
            ? This action cannot be undone and will permanently remove the backup file.
          </div>
        }
        confirmText="Delete Backup"
        confirmVariant="destructive"
        icon={Trash2}
      />

      {/* Bulk Backup Confirmation Modal */}
      <ConfirmationModal
        isOpen={showBulkBackupConfirm}
        onClose={() => setShowBulkBackupConfirm(false)}
        onConfirm={handleBulkBackupConfirm}
        isLoading={bulkBackupMutation.isLoading}
        title="Create Bulk Backup"
        message={
          <div>
            Are you sure you want to create backups for all{' '}
            <span className="font-medium">{clients?.length || 0} clients</span>
            ? This may take some time to complete.
          </div>
        }
        confirmText="Create Backups"
        confirmVariant="default"
        icon={Archive}
      />
    </div>
  )
}

export default ClientBackupManager
